import { useEffect, useRef, useState } from "react";
import Button from "react-bootstrap/Button";
import { Link } from "react-router-dom";
import { Alert } from "react-bootstrap";
import Form from 'react-bootstrap/Form';
import DatePickerInput from "../../feature/DatePickerInput";
import { format } from "date-fns";

import Translated from "../../feature/Translated";
import Spinner from "../../feature/Spinner";
import {
  useChangeOrderStatusToOk,
  useSearchWebshopOrders,
} from "../../../api-new";
import { useTranslation } from "../../feature/TranslationProvider";


const sleep = async (time) =>
  new Promise((res, rej) => {
    setTimeout(res, time);
  });

const AdminWebshopOrdersPage = () => {
  const { translate } = useTranslation();
  const [searchTerm, setSearchTerm] = useState("");
  const [searchType, setSearchType] = useState("name");
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [searchPlaceholder, setSearchPlaceholder] = useState(
    translate("search_type_name")
  );

  const changedOrderId = useRef(null);
  const changedOrderRef = useRef(null);
  const [queryTerm, setQueryTerm] = useState(searchTerm);
  const orderStatusMutation = useChangeOrderStatusToOk();
  const [querySearchType, setQuerySearchType] = useState(searchType);
  const ordersQuery = useSearchWebshopOrders(queryTerm, querySearchType);

  const maxDate = new Date();

  useEffect(() => {
    if (
      ordersQuery.isSuccess &&
      changedOrderId.current &&
      changedOrderRef.current
    ) {
      changedOrderRef.current.scrollIntoView();
    }
  }, [ordersQuery.data]);

  useEffect(() => {
    if (searchType === "name") {
      setSearchPlaceholder(translate("search_placeholder_name"));
    } else if (searchType === "email") {
      setSearchPlaceholder(translate("search_placeholder_email"));
    } else if (searchType === "phone") {
      setSearchPlaceholder(translate("search_placeholder_phone"));
    } else if (searchType === "date") {
      setSearchPlaceholder("");
    }
  }, [searchType, translate]);

  const isSearchDisabled =
    (searchType !== "date" && searchTerm && searchTerm.length < 3) ||
    (searchType === "date" && !(startDate && endDate));

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    if (isSearchDisabled) return;
    fetchOrders();
  };

  const fetchOrders = () => {
    changedOrderId.current = null;
    if (queryTerm === searchTerm && querySearchType === searchType) {
      ordersQuery.refetch();
    } else {
      setQuerySearchType(searchType);
      if (searchType === "date") {
        const start = startDate ? format(startDate, "yyyy-MM-dd") : "";
        const end = endDate ? format(endDate, "yyyy-MM-dd") : "";
        const term = `${start}|${end}`;
        setQueryTerm(term);
      } else {
        setQueryTerm(searchTerm);
      }
    }
  };

  const changeOrderStatusToOk = (orderId) => {
    orderStatusMutation.mutate(
      {
        orderId,
      },
      {
        onSuccess: () => {
          changedOrderId.current = orderId;
          ordersQuery.refetch();
        },
      }
    );
  };

  const renderOrders = () => {
    const orders = ordersQuery.data.orders;

    return orders.map((order) => {
      const isOrderStatusChanged = order.id === changedOrderId.current;
      const ref = isOrderStatusChanged ? changedOrderRef : null;
      const rows = Object.keys(order).map((key) => {
        if (key === "orderDataDump") {
          return null;
        }

        if (key === "status" && order.status !== "ok") {
          return (
            <div key={key}>
              <p>
                <strong>{key}: </strong>
                {order.status}&nbsp;
              </p>
              <Button
                onClick={() => {
                  changeOrderStatusToOk(order.id);
                }}
              >
                {translate("change_order_status_to_ok")}
                {orderStatusMutation.isPending ? <Spinner /> : null}
              </Button>
              {orderStatusMutation.isError && (
                <Alert>{translate(orderStatusMutation.error.message)}</Alert>
              )}
            </div>
          );
        }
        let value = JSON.stringify(order[key]);
        // Add some white space to make the text wrap.
        value = value.replace(/","/g, '", "');
        return (
          <div key={key}>
            <span className="strong">{key}</span>: {value}
          </div>
        );
      });
      const orderPageUrl =
        ordersQuery.data.homePageUrl + "/order/" + order.id + "/" + order.token;
      return (
        <div ref={ref} className="blue-box word-wrap" key={order.id}>
          {rows}
          <div>
            <a href={orderPageUrl} target="_blank" rel="noopener noreferrer">
              {translate("order_info_page")} &gt;&gt;
            </a>
          </div>
          <Link to={`/email-events/${order.customerEmail}`}>
            {translate("email_events_page")} &gt;&gt;
          </Link>
        </div>
      );
    });
  };

  const count = ordersQuery.isSuccess && (
    <div>
      {translate("total")}: {ordersQuery.data.orders.length}
    </div>
  );
  
  return (
    <div className="page-container max-width-container">
      <h2>
        <Translated translationKey="menu_webshop_orders" />
      </h2>
      <div id="user-search-container" className="blue-box">
        <Form onSubmit={handleSearchSubmit} className="fade-in">
            <div
              className="small-bottom-margin">
              <Form.Select
                aria-label="Select search type"
                style={{ width: 300 }}
                onChange={(e) => {
                  setSearchType(e.target.value)
                }}
              >
                <option value="name">{translate("search_type_name")}</option>
                <option value="email">{translate("search_type_email")}</option>
                <option value="phone">{translate("search_type_phone")}</option>
                <option value="date">{translate("search_type_date")}</option>
              </Form.Select>
            </div>
            {searchType === "date" && (
              <div
                style={{ flexDirection: "row", display: "flex", alignItems: "left", gap: "10px" }}
                className="small-bottom-margin">
                <div style={{ alignSelf: "center" }}>
                  {`${translate("start_date")}:`}
                </div>
                <DatePickerInput
                  selected={startDate}
                  onChange={(date) => setStartDate(date)}
                  maxDate={maxDate}
                  placeholderText={translate("start_date")}
                  isClearable
                  // className="right-margin"
                />
                <div style={{ alignSelf: "center" }}>
                  {`${translate("end_date")}:`}
                </div>
                <DatePickerInput
                  selected={endDate}
                  onChange={(date) => setEndDate(date)}
                  maxDate={maxDate}
                  placeholderText={translate("end_date")}
                  isClearable
                />
              </div>
            )}
            {searchType !== "date" &&
              (<Form.Control
                type="text"
                name="term"
                value={searchTerm}
                placeholder={searchPlaceholder}
                onChange={(e) => (setSearchTerm(e.target.value))}
                className="small-bottom-margin"
              />)}
          <Button
            type="submit"
            disabled={isSearchDisabled}
            id="search-button"
            // onClick={fetchOrders}
          >
            {translate("search_do")}
          </Button>
        </Form>
      </div>
      {(ordersQuery.isFetching || orderStatusMutation.isPending) && <Spinner />}
      {ordersQuery.isError && (
        <Alert>{translate(ordersQuery.error.message)}</Alert>
      )}
      {count}
      {ordersQuery.isSuccess && renderOrders()}
    </div>
  );
};

export default AdminWebshopOrdersPage;
